import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import auditEventsService from "../../../services/system/auditEventsService";
import "./SalesAuditPage.css";

const DEFAULT_FILTERS = {
  dataAreaId: "",
  order: "",
  externalKey: "",
  realUserId: "",
  from: "",
  to: "",
};

const pageSizeOptions = [25, 50, 100, 200];

const SalesAuditPage = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [pageSize, setPageSize] = useState(50);
  const [actionPage, setActionPage] = useState(0);
  const [result, setResult] = useState({ content: [], totalElements: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedAction, setSelectedAction] = useState(null);
  const filterPanelRef = useRef(null);
  const detailSlotRef = useRef(null);
  const [floatingDetailStyle, setFloatingDetailStyle] = useState(null);

  const actions = result?.content || [];
  const actionTotalPages = Math.max(1, result?.totalPages || 0);

  const loadEvents = async (activeFilters = filters, nextPage = actionPage) => {
    setLoading(true);
    setError("");
    try {
      const data = await auditEventsService.searchSalesActions({
        dataAreaId: activeFilters.dataAreaId,
        order: activeFilters.order,
        realUserId: activeFilters.realUserId,
        externalKey: activeFilters.externalKey,
        from: activeFilters.from,
        to: activeFilters.to,
      }, nextPage, pageSize);

      setResult(data || { content: [], totalElements: 0, totalPages: 0 });
      setActionPage(nextPage);
    } catch (err) {
      setError(err?.message || t("common.errorOccurred"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvents(filters, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize]);

  useLayoutEffect(() => {
    const updateFloatingDetail = () => {
      const detailSlot = detailSlotRef.current;
      const filterPanel = filterPanelRef.current;
      if (!detailSlot || !filterPanel || window.innerWidth <= 900) {
        setFloatingDetailStyle(null);
        return;
      }

      const detailRect = detailSlot.getBoundingClientRect();
      const filterRect = filterPanel.getBoundingClientRect();
      const top = Math.max(filterRect.bottom + 16, 120);
      const availableHeight = Math.max(260, window.innerHeight - top - 16);

      setFloatingDetailStyle({
        position: "fixed",
        top: `${top}px`,
        left: `${detailRect.left}px`,
        width: `${detailRect.width}px`,
        height: `${availableHeight}px`,
      });
    };

    updateFloatingDetail();
    window.addEventListener("resize", updateFloatingDetail);

    return () => {
      window.removeEventListener("resize", updateFloatingDetail);
    };
  }, [selectedAction, actions.length]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const submitFilters = (event) => {
    event.preventDefault();
    setSelectedAction(null);
    loadEvents(filters, 0);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSelectedAction(null);
    setTimeout(() => loadEvents(DEFAULT_FILTERS, 0), 0);
  };

  return (
    <div className="sales-audit-page">
      <header className="sales-audit-header">
        <div>
          <h1>{t("salesAudit.title")}</h1>
          <p>{t("salesAudit.subtitle")}</p>
        </div>
        <button className="btn btn-primary" type="button" onClick={() => loadEvents(filters, actionPage)} disabled={loading}>
          <i className="fa fa-refresh" aria-hidden="true"></i>
          <span>{t("salesAudit.refresh")}</span>
        </button>
      </header>

      <form ref={filterPanelRef} className="sales-audit-filter-panel" onSubmit={submitFilters}>
        <label>
          <span>{t("salesAudit.filters.company")}</span>
          <input className="form-control" value={filters.dataAreaId} onChange={(event) => setFilter("dataAreaId", event.target.value)} />
        </label>
        <label>
          <span>{t("salesAudit.filters.order")}</span>
          <input className="form-control" value={filters.order} onChange={(event) => setFilter("order", event.target.value)} />
        </label>
        <label>
          <span>{t("salesAudit.filters.externalKey")}</span>
          <input className="form-control" value={filters.externalKey} onChange={(event) => setFilter("externalKey", event.target.value)} />
        </label>
        <label>
          <span>{t("salesAudit.filters.user")}</span>
          <input className="form-control" value={filters.realUserId} onChange={(event) => setFilter("realUserId", event.target.value)} />
        </label>
        <label>
          <span>{t("salesAudit.filters.from")}</span>
          <input className="form-control" type="datetime-local" value={filters.from} onChange={(event) => setFilter("from", event.target.value)} />
        </label>
        <label>
          <span>{t("salesAudit.filters.to")}</span>
          <input className="form-control" type="datetime-local" value={filters.to} onChange={(event) => setFilter("to", event.target.value)} />
        </label>
        <label>
          <span>{t("salesAudit.filters.pageSize")}</span>
          <select className="form-control" value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
            {pageSizeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </label>
        <div className="sales-audit-filter-actions">
          <button className="btn btn-primary" type="submit" disabled={loading}>
            <i className="fa fa-search" aria-hidden="true"></i>
            <span>{t("common.search")}</span>
          </button>
          <button className="btn btn-default" type="button" onClick={resetFilters}>
            <i className="fa fa-eraser" aria-hidden="true"></i>
            <span>{t("salesAudit.clear")}</span>
          </button>
        </div>
      </form>

      {error && <div className="sales-audit-alert">{error}</div>}

      <section className="sales-audit-content">
        <div className="sales-audit-list">
          <div className="sales-audit-list-header">
            <h2>{t("salesAudit.actions")}</h2>
            <span>{t("salesAudit.visibleActions", { count: result?.totalElements || 0, total: result?.totalElements || 0 })}</span>
          </div>
          {loading ? (
            <div className="sales-audit-empty">{t("common.loading")}</div>
          ) : actions.length === 0 ? (
            <div className="sales-audit-empty">{t("salesAudit.noActions")}</div>
          ) : (
            <>
              <div className="sales-audit-timeline">
                {actions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    className={`sales-audit-action ${selectedAction?.id === action.id ? "selected" : ""}`}
                    onClick={() => setSelectedAction(action)}
                  >
                    <span className="sales-audit-action-icon"><i className={action.icon} aria-hidden="true"></i></span>
                    <span className="sales-audit-action-main">
                      <strong>{t(action.titleKey)}</strong>
                      <span>{describeAction(action, t)}</span>
                      {(action.changes || []).length > 0 && (
                        <span className="sales-audit-change-preview">
                          {(action.changes || []).map((change) => changeSummary(change, t)).join(" - ")}
                        </span>
                      )}
                    </span>
                    <span className="sales-audit-action-meta">
                      <span>{formatDateTime(action.eventTime)}</span>
                      <span>{action.user}</span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="sales-audit-pagination">
                <button className="btn btn-default" type="button" disabled={actionPage === 0 || loading} onClick={() => loadEvents(filters, 0)}>
                  <i className="fa fa-angle-double-left" aria-hidden="true"></i>
                </button>
                <button className="btn btn-default" type="button" disabled={actionPage === 0 || loading} onClick={() => loadEvents(filters, Math.max(0, actionPage - 1))}>
                  <i className="fa fa-angle-left" aria-hidden="true"></i>
                </button>
                <span>{t("common.pagination.page")} {actionPage + 1} / {actionTotalPages}</span>
                <button className="btn btn-default" type="button" disabled={actionPage + 1 >= actionTotalPages || loading} onClick={() => loadEvents(filters, Math.min(actionTotalPages - 1, actionPage + 1))}>
                  <i className="fa fa-angle-right" aria-hidden="true"></i>
                </button>
                <button className="btn btn-default" type="button" disabled={actionPage + 1 >= actionTotalPages || loading} onClick={() => loadEvents(filters, actionTotalPages - 1)}>
                  <i className="fa fa-angle-double-right" aria-hidden="true"></i>
                </button>
              </div>
            </>
          )}
        </div>

        <aside
          ref={detailSlotRef}
          className={`sales-audit-detail ${floatingDetailStyle ? "is-floating" : ""}`}
          style={floatingDetailStyle || undefined}
        >
          {selectedAction ? (
            <div className="sales-audit-detail-body">
              <div className="sales-audit-detail-header">
                <h2>{t(selectedAction.titleKey)}</h2>
                <span>{formatDateTime(selectedAction.eventTime)}</span>
              </div>
              <dl className="sales-audit-detail-grid">
                <Detail label={t("salesAudit.detail.iSalesId")} value={selectedAction.iSalesId} />
                <Detail label={t("salesAudit.detail.salesId")} value={selectedAction.salesId} />
                <Detail label={t("salesAudit.detail.line")} value={selectedAction.lineNum} />
                <Detail label={t("salesAudit.detail.company")} value={selectedAction.dataAreaId} />
                <Detail label={t("salesAudit.detail.customer")} value={selectedAction.customerLabel} />
                <Detail label={t("salesAudit.detail.user")} value={selectedAction.user} />
              </dl>
              <h3>{t("salesAudit.detail.changes")}</h3>
              {(selectedAction.changes || []).length === 0 ? (
                <div className="sales-audit-no-changes">{t("salesAudit.detail.noFieldChanges")}</div>
              ) : (
                <div className="sales-audit-change-list">
                  {(selectedAction.changes || []).map((change) => (
                    <div key={change.field} className="sales-audit-change-item">
                      <span>{t(change.labelKey)}</span>
                      <strong>{formatValue(change.from)}</strong>
                      <i className="fa fa-arrow-right" aria-hidden="true"></i>
                      <strong>{formatValue(change.to)}</strong>
                    </div>
                  ))}
                </div>
              )}
              <div className="sales-audit-detail-foot">
                <span>{t("salesAudit.detail.correlationId")}</span>
                <code>{selectedAction.correlationId || "-"}</code>
              </div>
            </div>
          ) : (
            <div className="sales-audit-empty-detail">
              <i className="fa fa-mouse-pointer" aria-hidden="true"></i>
              <span>{t("salesAudit.selectAction")}</span>
            </div>
          )}
        </aside>
      </section>
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <dt>{label}</dt>
    <dd>{value || "-"}</dd>
  </div>
);

const describeAction = (action, t) => {
  const order = action.salesId || action.iSalesId || action.orderLabel;
  const customer = action.customerLabel && action.customerLabel !== "-" ? action.customerLabel : "";
  if (order && customer) {
    return t("salesAudit.descriptions.orderAndCustomer", { order, customer });
  }
  if (order) {
    return t("salesAudit.descriptions.orderOnly", { order });
  }
  return t("salesAudit.descriptions.salesOrder");
};

const changeSummary = (change, t) => `${t(change.labelKey)}: ${formatValue(change.from)} -> ${formatValue(change.to)}`;
const formatDateTime = (value) => {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const text = String(value);
  if (text.startsWith("1900-01-01")) return "-";
  if (/^\d{4}-\d{2}-\d{2}T/.test(text)) {
    return formatDateOnly(text);
  }
  return text;
};

const formatDateOnly = (value) => {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

export default SalesAuditPage;
